import { STORE_STATE, toPromise, withStore } from '../storage/idb.js';
import type { StoredManifest } from '../manifest/types.js';

/**
 * What survives a power cut. The active manifest and the position in its loop are
 * written to disk so a cold boot plays the last known-good loop immediately, with no
 * network wait (technical-design §11.4, architecture-v2 §8.4, invariant 7).
 *
 * `pending` is a manifest that has been fetched but not yet fully cached and
 * verified. It is never played until it is complete, so `active` stays the rollback
 * target (technical-design §11.5).
 */

export interface PersistedPlayerState {
  readonly active: StoredManifest | null;
  readonly pending: StoredManifest | null;
  readonly loop_index: number;
  readonly clock_offset_ms: number;
  readonly boot_count: number;
  readonly last_error: string | null;
  readonly updated_at_device_ms: number;
}

export interface PlayerStateStore {
  load(): Promise<PersistedPlayerState | null>;
  save(state: PersistedPlayerState): Promise<void>;
}

export const EMPTY_PLAYER_STATE: PersistedPlayerState = {
  active: null,
  pending: null,
  loop_index: 0,
  clock_offset_ms: 0,
  boot_count: 0,
  last_error: null,
  updated_at_device_ms: 0,
};

export function createMemoryPlayerStateStore(
  initial: PersistedPlayerState | null = null,
): PlayerStateStore {
  let current = initial;
  return {
    load: async () => current,
    save: async (state) => {
      current = state;
    },
  };
}

const STATE_KEY = 'player';

interface StateRow {
  readonly key: string;
  readonly state: PersistedPlayerState;
}

export function createIdbPlayerStateStore(db: IDBDatabase): PlayerStateStore {
  return {
    load: async () => {
      const row = await withStore(db, STORE_STATE, 'readonly', (store) =>
        toPromise<StateRow | undefined>(store.get(STATE_KEY)),
      );
      return row?.state ?? null;
    },
    save: async (state) => {
      await withStore(db, STORE_STATE, 'readwrite', (store) => {
        store.put({ key: STATE_KEY, state } satisfies StateRow);
      });
    },
  };
}
